import React, { useState } from "react";
import { DISABILITY_TYPES } from "../../constants/disabilities";

const DisabilitySelect = ({
  selected = [],
  onChange,
  required = true,
  label = "Disability Type(s)",
  error = "",
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  const currentSelected = Array.isArray(selected)
    ? selected
    : typeof selected === "string" && selected.trim() !== ""
    ? selected.split(",").map((s) => s.trim()).filter(Boolean)
    : [];

  const toggleOption = (type) => {
    let next;
    if (currentSelected.includes(type)) {
      next = currentSelected.filter((t) => t !== type);
    } else {
      next = [...currentSelected, type];
    }
    onChange(next);
  };

  const selectAll = () => onChange([...DISABILITY_TYPES]);
  const clearAll = () => onChange([]);

  const filteredTypes = DISABILITY_TYPES.filter((t) =>
    t.toLowerCase().includes(searchTerm.toLowerCase().trim())
  );

  return (
    <div className="w-full text-left">
      <div className="flex items-center justify-between mb-1.5">
        <label className="block text-sm font-semibold text-slate-800">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <div className="flex items-center gap-2 text-xs">
          <button
            type="button"
            onClick={selectAll}
            className="text-blue-600 hover:underline font-medium"
          >
            Select All
          </button>
          <span className="text-slate-300">|</span>
          <button
            type="button"
            onClick={clearAll}
            className="text-slate-500 hover:underline font-medium"
          >
            Clear
          </button>
        </div>
      </div>

      <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5 mb-2 font-medium flex items-center gap-1.5 shadow-xs">
        <span>📌</span>
        <span>
          <strong>Note:</strong> Disability type(s) should be selected strictly according to your <strong>SADAREM / UDID Certificate</strong>.
        </span>
      </div>

      {currentSelected.length > 0 ? (
        <div className="flex flex-wrap gap-1.5 mb-2.5 p-2 bg-blue-50/60 border border-blue-200 rounded-lg min-h-[38px]">
          {currentSelected.map((item) => (
            <span
              key={item}
              className="inline-flex items-center gap-1 bg-blue-600 text-white text-xs font-medium px-2.5 py-1 rounded-full shadow-sm"
            >
              {item}
              <button
                type="button"
                onClick={() => toggleOption(item)}
                className="hover:bg-blue-700 rounded-full w-4 h-4 inline-flex items-center justify-center text-xs font-bold leading-none"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      ) : (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 mb-2 font-medium">
          ⚠️ Please select at least one disability type.
        </div>
      )}

      <div className="border border-slate-300 rounded-xl overflow-hidden bg-white shadow-inner">
        <div className="p-2 border-b border-slate-200 bg-slate-50">
          <input
            type="text"
            placeholder="Search disability types..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          />
        </div>

        <div className="max-h-48 overflow-y-auto p-2.5 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
          {filteredTypes.map((type) => {
            const isChecked = currentSelected.includes(type);
            return (
              <label
                key={type}
                className={`flex items-center gap-2 p-2 rounded-lg border transition cursor-pointer select-none ${
                  isChecked
                    ? "bg-blue-50 border-blue-400 text-blue-900 font-semibold"
                    : "border-slate-200 hover:bg-slate-50 text-slate-700"
                }`}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => toggleOption(type)}
                  className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
                />
                <span className="leading-snug">{type}</span>
              </label>
            );
          })}
        </div>
      </div>

      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
};

export default DisabilitySelect;
